<template>
  <div>
    <fieldset :disabled="$attrs.disabled">
      <!-- Basic Info -->
      <div class="card mt-5 p-md-4">
        <div class="card-body">
          <div class="card-title text-green">
            <feather-icon
              icon="InfoIcon"
              size="20"
            />
            <span class="b-inline-block ml-1">Basic Info</span>
          </div>
          <div class="card-text mb-2">
            <span class="form-section-description">
              Name your event and tell event-goers why they should come. Add details that highlight
              what makes it unique.
            </span>
          </div>
          <!-- Event Title -->
          <text-input
            id="event-title"
            name="Event Title"
            rules="max:200|required"
            :text.sync="event.title"
          />
          <!-- Organizer Name -->
          <b-row>
            <b-col md="10">
              <organizer-select
                rules="required"
                :select-first-option="true"
                :able-to-add-organizer="true"
                :organizer.sync="event.organizer"
                :disabled="$attrs.disabled"
              />
            </b-col>
            <b-col
              md="2"
              class="mt-2"
            >
              <b-link
                :to="{name:'organizer-view',params:{id:event.organizer.id}}"
                target="_blank"
              >
                View Organizer Profile
              </b-link>
            </b-col>
          </b-row>
          <div class="row">
            <div class="col-md-4">
              <!-- Event Type -->
              <types-select
                rules="required"
                :items.sync="event.type"
                :disabled="$attrs.disabled"
              />
            </div>
            <div class="col-md-4">
              <!-- Event Category -->
              <category-select
                rules="required"
                :disabled="$attrs.disabled"
                :items.sync="categoryProxy"
              />
            </div>
            <div class="col-md-4">
              <!-- Event Subcategory -->
              <subcategory-select
                :category="categoryId"
                :disabled="$attrs.disabled"
                :items.sync="event.sub_category"
              />
            </div>
          </div>
          <!-- Event Tags -->
          <tags-select
            multiple
            :items.sync="event.tags"
            taggable
            :disabled="$attrs.disabled"
          />
        </div>
      </div>
      <!-- Location -->
      <div class="card mt-2 p-md-4">
        <div class="card-body">
          <div class="card-title text-green">
            <feather-icon
              icon="MapPinIcon"
              size="20"
            />
            <span class="d-inline-block ml-1">Location</span>
          </div>
          <div class="card-text">
            <span class="form-section-description">
              Name your event and tell event-goers why they should come. Add details that highlight what makes it unique.
            </span>
          </div>
          <b-row class="my-2">
            <b-col
              md="4"
              xl="2"
            >
              <button
                class="btn btn-block btn-pill bg-light-green text-green mr-1"
                :class="{'text-white bg-primary':event.location_type==='offline'}"
                @click="changeLocationType('offline')"
              >
                Venue
              </button>
            </b-col>
            <b-col
              md="4"
              xl="2"
              class="my-1 my-md-0"
            >
              <button
                class="btn btn-block btn-pill bg-light-green text-green mr-1"
                :class="{'text-white bg-primary':event.location_type==='online'}"
                @click="changeLocationType('online')"
              >
                Online Event
              </button>
            </b-col>
            <b-col
              md="4"
              xl="3"
            >
              <button
                class="btn btn-block btn-pill bg-light-green text-green"
                :class="{'text-white bg-primary':event.location_type==='toBeAnnounced'}"
                @click="changeLocationType('toBeAnnounced')"
              >
                To Be Announced
              </button>
            </b-col>
          </b-row>
          <template
            v-if="event.location_type==='offline'"
          >
            <text-input
              id="venue-name"
              name="Venue Name"
              rules="required|max:500|min:3"
              :text.sync="event.venue.name"
            />
            <location-inputs
              rules="required"
              :details="event.venue.address"
            />
          </template>
          <text-input
            v-if="event.location_type==='online'"
            id="event-url"
            name="URL"
            rules="required|url"
            :text.sync="event.location_online"
          />
        </div>
      </div>
      <!-- Date and time -->
      <div class="card mt-5 p-md-4">
        <div class="card-body">
          <div class="card-title text-green">
            <feather-icon
              icon="CalendarIcon"
              size="20"
            />
            <span class="b-inline-block ml-1">Date and time</span>
          </div>
          <div class="card-text">
            <span class="form-section-description">
              Tell event-goers when your event starts and ends so they can make plans to attend.
            </span>
            <div class="row mt-2">
              <div class="col-md-6">
                <!-- Start Date -->
                <date-time
                  id="event-start-date"
                  :datetime.sync="event.start_date"
                  name="Event Starts"
                  rules="required"
                  :config="{ dateFormat: 'Y-m-d', minDate: 'today'}"
                />
              </div>
              <div class="col-md-6">
                <!-- Start Time -->
                <date-time
                  id="event-start-time"
                  :datetime.sync="event.start_time"
                  name="Start Time"
                  rules="required"
                  :config="{ enableTime:true,noCalendar:true,dateFormat: 'H:i' }"
                />
              </div>
            </div>
            <div class="row mb-4">
              <div class="col-md-6">
                <!-- End Date -->
                <date-time
                  id="event-end-date"
                  :datetime.sync="event.end_date"
                  name="Event Ends"
                  rules="required"
                  :config="{ dateFormat: 'Y-m-d', minDate: event.start_date}"
                />
              </div>
              <div class="col-md-6">
                <!-- End Time -->
                <date-time
                  id="event-end-time"
                  :datetime.sync="event.end_time"
                  name="End Time"
                  rules="required"
                  :config="{ enableTime:true,noCalendar:true,dateFormat: 'H:i' ,minTime:minEndTime}"
                />
              </div>
            </div>
            <!-- Display Start Time -->
            <b-form-group label-for="display-start-time">
              <b-checkbox
                id="display-start-time"
                v-model="event.is_start_time_displayed"
              >
                <div>Display Start Time</div>
                <small>The start time of your event will be displayed to attendees</small>
              </b-checkbox>
            </b-form-group>
            <!-- Display End Time -->
            <b-form-group
              label-for="display-end-time"
              class="mb-4"
            >
              <b-checkbox
                id="display-end-time"
                v-model="event.is_end_time_displayed"
              >
                <div>Display End Time</div>
                <small>The end time of your event will be displayed to attendees</small>
              </b-checkbox>
            </b-form-group>
            <b-row>
              <!-- Time Zone -->
              <b-col md="3">
                <timezones-select
                  rules="required"
                  :disabled="$attrs.disabled"
                  :items.sync="event.timezone"
                />
              </b-col>
            </b-row>
            <b-row>
              <!-- Language -->
              <b-col md="3">
                <languages-select
                  label="Event page language"
                  rules="required"
                  :disabled="$attrs.disabled"
                  :items.sync="event.language"
                />
              </b-col>
            </b-row>
            <b-row>
              <!-- Currency -->
              <b-col md="3">
                <currency-select
                  :items.sync="event.currency"
                  :disabled="$attrs.disabled"
                />
              </b-col>
            </b-row>
          </div>
        </div>
      </div>
    </fieldset>
  </div>
</template>

<script>
import Organizations from '@/compositions/Organizations/OrganizationsAPI'
import LocationInputs from '../common/LocationInputs.vue'
import DateTime from '../FormInputs/DateTime.vue'
import TextInput from '../FormInputs/TextInput.vue'
import CategorySelect from '../GeneralSettings/Categories/CategorySelect.vue'
import CurrencySelect from '../GeneralSettings/Currencies/CurrencySelect.vue'
import LanguagesSelect from '../GeneralSettings/Languages/LanguagesSelect.vue'
import SubcategorySelect from '../GeneralSettings/Subcategories/SubcategorySelect.vue'
import TagsSelect from '../GeneralSettings/Tags/TagsSelect.vue'
import TimezonesSelect from '../GeneralSettings/Timezones/TimezonesSelect.vue'
import TypesSelect from '../GeneralSettings/Types/TypesSelect.vue'
import OrganizerSelect from '../Organizer/OrganizerSelect.vue'

export default {
  name: 'EventInputs',
  components: {
    TextInput,
    DateTime,
    TypesSelect,
    CategorySelect,
    SubcategorySelect,
    TagsSelect,
    LocationInputs,
    TimezonesSelect,
    LanguagesSelect,
    OrganizerSelect,
    CurrencySelect,
  },
  props: {
    event: { type: Object, default: () => ({}) },
  },
  computed: {
    categoryProxy: {
      get() {
        return this.event.category
      },
      set(value) {
        this.$set(this.event, 'category', value)
        this.$set(this.event, 'sub_category', null)
      },
    },
    categoryId() {
      return this.event.category?.id || null
    },
    minEndTime() {
      return this.event.start_date === this.event.end_date ? this.$moment(`2020-01-01 ${this.event.start_time}`).add({ minute: 5 }).format('HH:mm') : null
    },
  },
  watch: {
    'event.start_date': function (newValue, oldValue) {
      this.resetEndDatetime(oldValue)
    },
    'event.start_time': function (newValue, oldValue) {
      this.resetEndDatetime(oldValue)
    },
    'event.end_date': function (newValue, oldValue) {
      this.resetEndTime(oldValue)
    },
  },
  setup() {
    const { getOrganizationRequest } = Organizations()
    return { getOrganizationRequest }
  },
  created() {
    this.getOrganization()
  },
  methods: {
    getOrganization() {
      if (!Object.keys(this.event.currency).length) {
        this.getOrganizationRequest(this.$orgId, { embed: 'currency' }).then(res => {
          this.$set(this.event, 'currency', res.data.data.currency || {})
        })
      }
    },
    changeLocationType(type) {
      if (type === 'online') {
        this.$set(this.event, 'venue', null)
      } else if (type === 'offline') {
        this.$set(this.event, 'location_url', null)
        this.$set(this.event, 'venue', { address: {} })
      } else if (type === 'toBeAnnounced') {
        this.$set(this.event, 'location_url', null)
        this.$set(this.event, 'venue', null)
      }
      this.$set(this.event, 'location_type', type)
    },
    resetEndDatetime(oldValue) {
      if (oldValue) {
        this.$set(this.event, 'end_date', null)
        this.$set(this.event, 'end_time', null)
      }
    },
    resetEndTime(oldValue) {
      if (oldValue) {
        this.$set(this.event, 'end_time', null)
      }
    },
  },
}
</script>

<style>

</style>
